import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { customerService, deviceService } from '../services/api'
import type { CustomerDeviceRecord, CustomerWithDevicesRecord, DeviceRecord } from '../types'

export type EditableDevice = DeviceRecord | CustomerDeviceRecord

type Props = {
  open: boolean
  device: EditableDevice | null
  /** مطلوب عند التحرير من صفحة العملاء إن لم يكن في السجل */
  customerId?: string
  mainDevices?: Array<{ id: string; activationCode?: string; computerName?: string | null; deviceCode?: string }>
  saving?: boolean
  onClose: () => void
  onSaved: () => void | Promise<void>
  onError: (message: string) => void
}

type FormState = {
  meraApiUrl: string
  notes: string
  version: string
  status: 'Active' | 'Inactive' | 'Suspended'
  computerName: string
  computerRole: string
  dbPasswordOverride: string
  clearDbPassword: boolean
  mainDeviceId: string
  apiHostingKind: 'Windows' | 'Host'
}

const emptyForm: FormState = {
  meraApiUrl: '',
  notes: '',
  version: '1.0.0',
  status: 'Inactive',
  computerName: '',
  computerRole: '',
  dbPasswordOverride: '',
  clearDbPassword: false,
  mainDeviceId: '',
  apiHostingKind: 'Windows',
}

const resolveCustomerId = (device: EditableDevice | null, fallback?: string) => {
  if (!device) return fallback || ''
  if ('customerId' in device && device.customerId) return device.customerId
  return fallback || ''
}

export default function EditDeviceDialog({
  open,
  device,
  customerId,
  mainDevices = [],
  saving: savingExternal,
  onClose,
  onSaved,
  onError,
}: Props) {
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [loadedMains, setLoadedMains] = useState<Props['mainDevices']>([])
  const busy = saving || !!savingExternal
  const mains = (mainDevices && mainDevices.length > 0 ? mainDevices : loadedMains) || []

  useEffect(() => {
    if (!open || !device) return
    const cid = resolveCustomerId(device, customerId)
    if (device.type === 'Api' && (!mainDevices || mainDevices.length === 0) && cid) {
      void customerService.getWithDevices(cid).then((response: any) => {
        const payload = (response?.data ?? response) as CustomerWithDevicesRecord
        const mainsOnly = (payload?.devices || []).filter((d) => d.type === 'Main')
        setLoadedMains(mainsOnly.map((d) => ({
          id: d.id,
          activationCode: d.activationCode,
          computerName: d.computerName,
          deviceCode: d.deviceCode,
        })))
      }).catch(() => setLoadedMains([]))
    } else {
      setLoadedMains([])
    }
    setForm({
      meraApiUrl: device.meraApiUrl || '',
      notes: device.notes || '',
      version: device.version || '1.0.0',
      status: device.status,
      computerName: device.computerName || '',
      computerRole: device.computerRole || '',
      dbPasswordOverride: '',
      clearDbPassword: false,
      mainDeviceId: device.mainDeviceId || '',
      apiHostingKind: device.apiHostingKind === 'Host' ? 'Host' : 'Windows',
    })
  }, [open, device])

  const save = async () => {
    if (!device) return
    const cid = resolveCustomerId(device, customerId)
    if (!cid) {
      onError('معرف العميل غير متوفر')
      return
    }

    try {
      setSaving(true)
      await deviceService.update(device.id, {
        customerId: cid,
        type: device.type,
        mainDeviceId: device.type === 'Api'
          ? (form.mainDeviceId || undefined)
          : (device.mainDeviceId ?? undefined),
        apiHostingKind: device.type === 'Api' ? form.apiHostingKind : undefined,
        status: form.status,
        version: form.version.trim() || device.version,
        subscriptionType: device.subscriptionType,
        subscriptionDurationMonths: device.subscriptionDurationMonths ?? undefined,
        renewalWarningDays:
          'renewalWarningDays' in device && typeof device.renewalWarningDays === 'number'
            ? device.renewalWarningDays
            : 3,
        gracePeriodDays:
          'gracePeriodDays' in device && typeof device.gracePeriodDays === 'number'
            ? device.gracePeriodDays
            : 3,
        notes: form.notes.trim() || null,
        meraApiUrl: form.meraApiUrl.trim() || null,
        computerName: form.computerName.trim() || null,
        computerRole: form.computerRole.trim() || null,
        useIndependentReportBranding:
          device.type === 'Mobile' || device.type === 'MeraApp'
            ? !!device.useIndependentReportBranding
            : undefined,
      })

      if (device.type === 'Api' && (form.dbPasswordOverride.trim() || form.clearDbPassword)) {
        await deviceService.updateSiteSettings(device.id, {
          dbPasswordOverride: form.clearDbPassword ? null : form.dbPasswordOverride.trim() || null,
        })
      }

      await onSaved()
      onClose()
    } catch (error: any) {
      onError(error?.response?.data?.message || error?.message || 'تعذر حفظ تعديلات الجهاز')
    } finally {
      setSaving(false)
    }
  }

  if (!device) return null

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>تعديل الجهاز — {device.activationCode}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            عدّل عنوان Mera API (الإيبي) والتفاصيل. الحاسبة تأخذ عنوان الـ API من الموقع عند الاتصال.
          </Alert>
          <TextField
            label="عنوان Mera API"
            value={form.meraApiUrl}
            onChange={(e) => setForm((p) => ({ ...p, meraApiUrl: e.target.value }))}
            fullWidth
            placeholder={
              device.type === 'Mobile' || device.type === 'MeraApp'
                ? 'http://192.168.0.100:5078'
                : 'http://localhost:5078'
            }
            helperText="مثال: http://192.168.1.10:5078 — ليس 0.0.0.0"
          />
          <TextField
            label="الإصدار"
            value={form.version}
            onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))}
            fullWidth
          />
          <TextField
            select
            label="الحالة"
            value={form.status}
            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as FormState['status'] }))}
            fullWidth
          >
            <MenuItem value="Active">مفعّل</MenuItem>
            <MenuItem value="Inactive">بانتظار التفعيل</MenuItem>
            <MenuItem value="Suspended">موقوف</MenuItem>
          </TextField>
          {(device.type === 'Main' || device.type === 'Sub') && (
            <>
              <TextField
                label="اسم/رقم الحاسبة (الستنك)"
                value={form.computerName}
                onChange={(e) => setForm((p) => ({ ...p, computerName: e.target.value }))}
                fullWidth
                placeholder="PC-01"
              />
              <TextField
                select
                label="دور الحاسبة"
                value={form.computerRole || (device.type === 'Sub' ? 'Sub' : 'Main')}
                onChange={(e) => setForm((p) => ({ ...p, computerRole: e.target.value }))}
                fullWidth
              >
                <MenuItem value="Main">رئيسية</MenuItem>
                <MenuItem value="Sub">فرعية</MenuItem>
              </TextField>
            </>
          )}
          {device.type === 'Api' && (
            <>
              <TextField
                select
                label="الجهاز الرئيسي المرتبط"
                value={form.mainDeviceId}
                onChange={(e) => setForm((p) => ({ ...p, mainDeviceId: e.target.value }))}
                fullWidth
                helperText="العملاء الحاليون: اربط الـ API بالحاسبة الرئيسية من هنا."
              >
                <MenuItem value="">بدون ربط</MenuItem>
                {mains.map((main) => (
                  <MenuItem key={main.id} value={main.id}>
                    {main.computerName || main.activationCode || main.deviceCode}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="أين منتصب الـ API؟"
                value={form.apiHostingKind}
                onChange={(e) => setForm((p) => ({ ...p, apiHostingKind: e.target.value as 'Windows' | 'Host' }))}
                fullWidth
                helperText="الافتراضي ويندوز. غيّر إلى هوست فقط إذا أنت رفعته على Plesk ثم أشّره محدّثاً بعد الرفع."
              >
                <MenuItem value="Windows">ويندوز على الحاسبة الرئيسية</MenuItem>
                <MenuItem value="Host">هوست (Plesk) — تحديث يدوي</MenuItem>
              </TextField>
            </>
          )}
          {device.type === 'Api' && (
            <TextField
              label="باسوورد الداتابيس (API)"
              type="password"
              value={form.dbPasswordOverride}
              onChange={(e) => setForm((p) => ({ ...p, dbPasswordOverride: e.target.value, clearDbPassword: false }))}
              fullWidth
              helperText={
                device.hasDbPasswordOverride
                  ? 'اتركه فارغاً للإبقاء على المخصّص الحالي، أو اكتب جديداً. لمسح المخصّص استخدم الزر أدناه.'
                  : 'اختياري — وإلا الافتراضي من إعدادات الموقع'
              }
            />
          )}
          {device.type === 'Api' && device.hasDbPasswordOverride && (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={busy}
              onClick={() => setForm((p) => ({ ...p, clearDbPassword: true, dbPasswordOverride: '' }))}
            >
              مسح باسوورد الداتابيس المخصّص (العودة لافتراضي الموقع)
            </Button>
          )}
          <TextField
            label="تفاصيل التفعيل / ملاحظات"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={busy}>إلغاء</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? 'جاري الحفظ...' : 'حفظ التعديلات'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
